import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';

import { refreshToken } from 'services/auth';
import { useAppDispatch } from 'store/hooks';
import { ThunkGetUserData } from 'store/user/thunks';
import { ThunkGetPusherConfig } from 'store/chats/thunks';
import { setUserRole } from 'store/user';

import { HOME } from 'router/routes';
import { LOCAL_STORAGE_REFRESH_TOKEN, LOCAL_STORAGE_TOKEN } from 'constants/storage';
import { Roles } from 'constants/roles';

import AppBarComponent from './components/AppBar';
import SideBar from './components/SideBar';
import Loader from '../Loader';

import useStyles from './styles';

interface IProps {
  noLayout: boolean;
}

const Layout: React.FC<IProps> = ({ noLayout = false }) => {
  const [loading, setLoading] = useState<boolean>(true);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);
  const refresh = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN);

  const onMount = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      dispatch(ThunkGetPusherConfig()).unwrap(),
      dispatch(ThunkGetUserData()).unwrap(),
    ]).finally(() => {
      setLoading(false);
    });
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/') {
      navigate(HOME);
    }
  }, [location]);

  useEffect(() => {
    if (token) {
      const interval = setInterval(() => {
        refreshToken({ refresh_token: refresh ?? '', access_token: token ?? '' }).then(res => {
          localStorage.setItem(LOCAL_STORAGE_TOKEN, res.data.access_token);
          localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, res.data.refresh_token);
        });
      }, 420000);

      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);

    if (token) {
      const decoded: {
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
      } = jwtDecode(token);

      if (decoded) {
        dispatch(
          setUserRole(
            decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] as Roles,
          ),
        );
      }
    }
  }, []);

  if (loading) return <Loader />;
  if (noLayout) {
    return (
      <div className={classes.noLayoutWrapper}>
        <Outlet />
      </div>
    );
  }
  return (
    <div className={classes.layout}>
      <SideBar />
      <div className={classes.contentWrapper}>
        <div className={classes.shadowWrapper} >
          <AppBarComponent />
          <div className={classes.mainContent}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
